// File: frontend/src/contexts/NotificationContext.js

import React, { createContext, useState, useContext } from 'react';
import { useLanguage } from './LanguageContext';

const NotificationContext = createContext();

export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

export const NotificationProvider = ({ children }) => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    // Generate a unique ID for the notification
    const id = Date.now();
    
    // Set default values if not provided
    const type = notification.type || NOTIFICATION_TYPES.INFO;
    const title = notification.title || getDefaultTitle(type, t);
    const message = notification.message || '';
    const duration = notification.duration || 5000;
    
    // Add the notification to the state
    setNotifications(prev => [
      ...prev, 
      { 
        ...notification, 
        id, 
        type, 
        title, 
        message,
        createdAt: new Date()
      }
    ]);
    
    // Auto-remove the notification after the specified duration
    if (duration !== Infinity) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };
  
  const clearAllNotifications = () => {
    setNotifications([]);
  };
  
  // Helper function to get default titles based on notification type
  const getDefaultTitle = (type, t) => {
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return t('notification_success') || 'Success';
      case NOTIFICATION_TYPES.ERROR:
        return t('notification_error') || 'Error';
      case NOTIFICATION_TYPES.WARNING:
        return t('notification_warning') || 'Warning';
      case NOTIFICATION_TYPES.INFO:
      default:
        return t('notification_info') || 'Information';
    }
  };

  // Convenience methods for different notification types
  const success = (message, options = {}) => {
    return addNotification({ 
      type: NOTIFICATION_TYPES.SUCCESS, 
      message, 
      ...options 
    });
  };
  
  const error = (message, options = {}) => {
    return addNotification({ 
      type: NOTIFICATION_TYPES.ERROR, 
      message, 
      ...options 
    });
  };
  
  const warning = (message, options = {}) => {
    return addNotification({ 
      type: NOTIFICATION_TYPES.WARNING, 
      message, 
      ...options 
    });
  };
  
  const info = (message, options = {}) => {
    return addNotification({ 
      type: NOTIFICATION_TYPES.INFO, 
      message, 
      ...options 
    });
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      clearAllNotifications,
      success,
      error,
      warning,
      info
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
